<script>
	import { render_micro_markdown } from '#lib/util.js';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import { toast } from '#lib/toast.svelte.js';
  import { onDestroy } from 'svelte';
  
  const type_to_icon = {
    success: 'check',
    error: 'close',
    info: null,
    warning: 'warning'
  };


  let grouped_messages = $derived.by(() => {
    const groups = {};
    toast.messages.forEach(message => {
      if (!groups[message.placement]) {
        groups[message.placement] = [];
      }
      groups[message.placement].push(message);
    });
    return groups;
  });

  $effect(() => {
    toast.messages.length > 0 ? toast.toast_ref.showPopover() : toast?.toast_ref?.hidePopover();
  });

  // A toast belongs to the surface that raised it. Leaving the page takes the
  // toasts still showing with it, instead of playing them again on Back.
  onDestroy(() => toast.clear());
</script>

{#if toast.messages.length > 0}
  <!-- NOTE: in popover="manual" mode the toast container is not placed in the top layer, with auto mode it is, but then any click outside the toast container will dismiss the toast -->
  <div 
    class="toast-container"
    popover="manual"
    bind:this={toast.toast_ref}
  >
    {#each Object.entries(grouped_messages) as [placement, messages]}
      <div 
        class="toast-group placement-{placement} flex-column gap-y g-2"
        role="presentation"
        onpointerenter={() => toast.pauseTimers(placement)}
        onpointerleave={() => toast.resumeTimers(placement)}
      >
        {#each messages as message (message.id)}
          {#if message.content}
            <svelte:element
              this={!message.show_close_icon && message.dismissible ? 'button' : 'div'}
              {...(!message.show_close_icon && message.dismissible ? { onclick: () => toast.remove(message.id) } : {})}
              id={`toast_${message.id}`}
              class="toast-message {message.type} placement-{message.placement} {message.removing ? 'removing' : ''} bg border-sm shadow-md"
              class:updating={message.updating}
            >
              <div 
                class="toast-inner flex-row items-center"
                role="alert"
              >
                {#if type_to_icon[message.type]}
                  <ButtonLabel class='toast-type-icon' start_icon={type_to_icon[message.type]} />
                {/if}
                <div class="toast-content text-body flex-column">
                  {@html render_micro_markdown(message.content)}
                </div>
                {#if message.show_close_icon && message.dismissible}
                  <button class="dismiss" onclick={() => toast.remove(message.id)}>
                    <ButtonLabel start_icon="close" />
                  </button>
                {/if}
              </div>
            </svelte:element>
          {/if}
        {/each}
      </div>
    {/each}
  </div>
{/if}

<style lang="postcss">
  .toast-container {
    /* The toasts' distance from the edges of the screen */
    --toast-inset: var(--size-5);
    position: fixed;
    z-index: 9999;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background: transparent;
    backdrop-filter: none;
    /* The toasts measure themselves against the screen (see .toast-message) */
    container-type: inline-size;
    display: grid;
    /* The middle column is as wide as the toasts standing in it; the outer
       columns share the rest. Three equal columns gave a top toast a third
       of a phone screen: a narrow, tall block of text. */
    grid-template-columns: 1fr auto 1fr;
    grid-template-rows: repeat(2, 1fr);
    grid-template-areas:
      'top-left top top-right'
      'bottom-left bottom bottom-right';
    &::backdrop {
      display: none;
    }
  }

  .toast-group {
    display: flex;
    padding: var(--toast-inset);
    /* placement */
    &.placement-top-left {
      grid-area: top-left;
      align-items: flex-start;
      justify-content: flex-start;
    }
    &.placement-top {
      grid-area: top;
      align-items: center;
      justify-content: flex-start;
      .toast-content {
        text-align: center;
      }
    }
    &.placement-top-right {
      grid-area: top-right;
      align-items: flex-end;
      justify-content: flex-start;
    }
    &.placement-bottom-left {
      grid-area: bottom-left;
      align-items: flex-start;
      justify-content: flex-end;
    }
    &.placement-bottom {
      grid-area: bottom;
      align-items: center;
      justify-content: flex-end;
      .toast-content {
        text-align: center;
      }
    }
    &.placement-bottom-right {
      grid-area: bottom-right;
      align-items: flex-end;
      justify-content: flex-end;
    }
  }

  .toast-message {
    --in-duration: 0.22s;
    --out-duration: 0.7s;
    display: grid;
    grid-template-rows: 1fr;
    border: 1px solid var(--color-default-stroke);
    /* A readable measure on a wide screen, the full width on a phone */
    --toast-max-width: calc(100cqw - 2 * var(--toast-inset));
    min-width: min(20ch, var(--toast-max-width));
    max-width: min(34rem, var(--toast-max-width));

    /* exit transition */
    &.removing {
      grid-template-rows: 0fr;
      box-shadow: 0 0 0 0 transparent;
      border-color: transparent;
      transition-duration: var(--out-duration);
      * {
        opacity: 0;
      }
    }

    /* default state */
    opacity: 1;
    transform: translateY(0) scale(1);
    transition: all var(--in-duration) cubic-bezier(0.21, 1.02, 0.73, 1);
    transition-behavior: allow-discrete;
    * {
      transition: opacity calc(var(--out-duration) * 0.5) cubic-bezier(0.21, 1.02, 0.73, 1);
    }

    /* enter transition */
    @media (prefers-reduced-motion: no-preference) {
      &:is(.placement-top-left, .placement-top, .placement-top-right) {
        @starting-style {
          opacity: 0;
          transform: translateY(-100%) scale(0.6);
        }
      }

      &:is(.placement-bottom-left, .placement-bottom, .placement-bottom-right) {
        @starting-style {
          opacity: 0;
          transform: translateY(100%) scale(0.6);
        }
      }
    }

    &.info :global(.toast-type-icon) {
      --button-icon-color1: var(--color-default-foreground);
    }
    &.success :global(.toast-type-icon) {
      --button-icon-color1: var(--editing);
    }
    &.warning :global(.toast-type-icon) {
      --button-icon-color1: var(--color-destructive);
    }
    &.error {
      background-color: var(--color-destructive);
      border-color: oklch(from var(--color-destructive) calc(l * 0.9) c h);
      --button-icon-color1: var(--color-default-background);
      * {
        color: var(--color-default-background);
        text-rendering: geometricPrecision; /** lightens the font-weight */
      }
    }

    .toast-inner {
      display: grid;
      grid-template-columns: auto 1fr auto;
      pointer-events: auto;
      white-space: pre-line;
      overflow: hidden;
      :global(.button-label + .toast-content) {
        padding-inline-start: 0;
      }
      .toast-content {
        grid-column: 2 / 3;
        padding: var(--size-2) var(--size-4);
        width: 100%;
        &:has(+ .dismiss) {
          padding-inline-end: 0;
        }
      }
      .dismiss {
        grid-column: 3 / 4;
      }
    }

    &.updating {
      animation: flash 0.5s ease-out;
      /* background-color: var(--color-default-emphasis-background); */
    }
  }

  @keyframes flash {
    0% {
      transform: scale(1);
      filter: brightness(100%);
    }
    50% {
      transform: scale(1.02);
      filter: brightness(95%);
    }
    100% {
      transform: scale(1);
      filter: brightness(100%);
    }
  }

  .dismiss {
    margin-inline-start: auto;
    width: fit-content;
    align-self: flex-start;
  }

</style>